/**
 * The dock's title bar: conversations toggle, the Kelly mark, New chat, Expand and Close. A pure presenter —
 * every action is a prop, so the dock host owns all of the state. There is deliberately no context label
 * ("looking at <resource>"): context awareness is a confirmed fast-follow and there is nothing to show yet.
 */

import type { Ref } from "react"
import { Add, AutoAwesome, Close, CloseFullscreen, Menu, OpenInFull } from "@mui/icons-material"
import { Box, IconButton, Tooltip, Typography } from "@mui/material"
import type { SxProps, Theme } from "@mui/material"
import { TOP_BAR_HEIGHT } from "../geometry/dock-metrics"

// Fixed to the app bar's height rather than sized by its buttons: the dock sits at `top: 0`, so this row and
// the bar are the same band of the screen and their bottom hairlines have to meet. `content-box` against the
// global border-box reset is what puts the hairline BELOW the row, where the bar's own sits (on the AppBar's
// border, under its row) — inside it, Kelly's line lands a pixel high.
const HEADER_SX = { display: "flex", alignItems: "center", gap: 0.25, px: 0.5, height: TOP_BAR_HEIGHT, boxSizing: "content-box", flexShrink: 0, borderBottom: 1, borderColor: "divider" } satisfies SxProps<Theme>
const MARK_SX = { display: "flex", alignItems: "center", gap: 0.5, flex: 1, minWidth: 0, ml: 0.5 } satisfies SxProps<Theme>
const MARK_ICON_SX = { color: "primary.main" } satisfies SxProps<Theme>
const MARK_TEXT_SX = { fontWeight: 700, color: "primary.main" } satisfies SxProps<Theme>

/**
 * Anchor + hover handlers for the host's conversations peek. The header stays a presenter: it neither owns the
 * preview's state nor knows there is one — it hands its ☰ over as an anchor and a hover source.
 */
export interface ConversationsTriggerProps {
  ref?: Ref<HTMLButtonElement>
  onMouseEnter?: () => void
  onMouseLeave?: () => void
}

export interface DockHeaderProps {
  isConversationsOpen: boolean
  isFull: boolean
  conversationsTriggerProps: ConversationsTriggerProps
  onToggleConversations: () => void
  onNewChat: () => void
  onToggleFull: () => void
  onClose: () => void
}

export function DockHeader({ isConversationsOpen, isFull, conversationsTriggerProps, onToggleConversations, onNewChat, onToggleFull, onClose }: DockHeaderProps) {
  return (
    <Box sx={HEADER_SX} data-testid="kelly-dock-header">
      {/* Full screen seats the conversations drawer beside the transcript, and that drawer is then the only
          navigator — a header toggle for a list already on screen would be the second. */}
      {isFull ? null : (
        // disableHoverListener: hover belongs to the host's peek — a tooltip opening in the same spot after its
        // delay sits in the pointer path and fires the preview's leave handlers (same trap as the rail's +).
        <Tooltip title="Conversations" disableHoverListener>
          <IconButton
            aria-label="Conversations"
            aria-expanded={isConversationsOpen}
            data-testid="kelly-dock-conversations"
            onClick={onToggleConversations}
            {...conversationsTriggerProps}
          >
            <Menu />
          </IconButton>
        </Tooltip>
      )}
      <Box sx={MARK_SX}>
        <AutoAwesome sx={MARK_ICON_SX} />
        <Typography variant="h6" sx={MARK_TEXT_SX} noWrap>Kelly</Typography>
      </Box>
      <Tooltip title="New chat">
        <IconButton aria-label="New chat" data-testid="kelly-dock-new-chat" onClick={onNewChat}>
          <Add />
        </IconButton>
      </Tooltip>
      {/* Always present, in both views: full screen covers the top chrome, so this is the exit affordance that
          does not depend on the nav rail being visible behind it. */}
      <Tooltip title={isFull ? "Minimise" : "Expand"}>
        <IconButton
          aria-label={isFull ? "Minimise Kelly" : "Expand Kelly"}
          data-testid="kelly-dock-toggle-full"
          onClick={onToggleFull}
        >
          {isFull ? <CloseFullscreen /> : <OpenInFull />}
        </IconButton>
      </Tooltip>
      <Tooltip title="Close">
        <IconButton aria-label="Close Kelly" data-testid="kelly-dock-close" onClick={onClose}>
          <Close />
        </IconButton>
      </Tooltip>
    </Box>
  )
}
